import React, {
    lazy, memo, useCallback, useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

import { FormFieldTypes, validationTypes } from "@repo/design-system/components/forms/libs";
import { getInputTypeByFieldType } from "@repo/design-system/components/forms/utils";

// Resolves a config value that is either:
//   - a plain i18n key string (contains a dot): translated directly
//   - an object { key, params } for interpolated translations
//   - a literal value (no dot): returned as-is
//
// Supports two interpolation styles used in this project:
//   {{placeholder}} — i18next standard, handled via t(key, params)
//   {0}            — legacy single-brace style, handled via regex replace
const resolveI18n = (value, t) => {
    if (!value && value !== 0) return value;
    if (typeof value === "object" && value !== null && value.key) {
        // Resolve any nested translation keys in params first
        const resolvedParams = value.params
            ? Object.fromEntries(
                Object.entries(value.params).map(([k, v]) => [
                    k,
                    typeof v === "string" && v.includes(".") ? t(v) : v,
                ])
            )
            : {};
        // Let i18next handle {{key}} interpolation natively
        let result = t(value.key, resolvedParams);
        // Fallback: replace {key} single-brace style (not i18next standard)
        // Negative lookaround ensures we never touch {{key}} double-brace patterns
        Object.entries(resolvedParams).forEach(([k, v]) => {
            result = result.replace(
                new RegExp(`(?<!\\{)\\{${k}\\}(?!\\})`),
                String(v)
            );
        });
        return result;
    }
    if (typeof value === "string" && value.includes(".")) return t(value);
    return value;
};

// Components
import FormInputControls from "./fieldsControl/FormInputControls";
import FormCheckBoxControls from "./fieldsControl/FormCheckBoxControls";
import FormDatePickerControls from "./fieldsControl/FormDatePickerControls";
import FormDropdownControls from "./fieldsControl/FormDropdownControls";
import FormTextAreaControls from "./fieldsControl/FormTextAreaControls";
import FormFileUploadControls from "./fieldsControl/FormFileUploadControls";
import FormRadioButtonControls from "./fieldsControl/FormRadioButtonControls";
import FormEditableDropdownControls from "./fieldsControl/FormEditableDropdownControls";
import FormPhoneInputControls from "./fieldsControl/FormPhoneInputControls";
import FormImageUploadControls from "./fieldsControl/FormImageUploadControls";
import FormTimePickerControls from "./fieldsControl/FormTimePickerControls";
import FormLazyDropdownControls from "./fieldsControl/FormLazyDropdownControls";
import FormToggleControls from "./fieldsControl/FormToggleControls";
import FormRangeSliderControls from "./fieldsControl/FormRangeSliderControls";

const FormCkEditorControls = lazy(() => import("./fieldsControl/FormCkEditorControls"));
// Map
const FIELD_CONTROL_MAP = {
    [FormFieldTypes.INPUT]: FormInputControls,
    [FormFieldTypes.PASSWORD]: FormInputControls,
    [FormFieldTypes.NUMERIC]: FormInputControls,
    [FormFieldTypes.CHECKBOX]: FormCheckBoxControls,
    [FormFieldTypes.DATEPICKER]: FormDatePickerControls,
    [FormFieldTypes.SELECT]: FormDropdownControls,
    [FormFieldTypes.TEXTAREA]: FormTextAreaControls,
    [FormFieldTypes.TEXTEDITOR]: FormCkEditorControls,
    [FormFieldTypes.FILE]: FormFileUploadControls,
    [FormFieldTypes.RADIOBUTTON]: FormRadioButtonControls,
    [FormFieldTypes.EDITABLEDROPDOWN]: FormEditableDropdownControls,
    [FormFieldTypes.PHONE]: FormPhoneInputControls,
    [FormFieldTypes.IMAGE]: FormImageUploadControls,
    [FormFieldTypes.TIME]: FormTimePickerControls,
    [FormFieldTypes.LAZY_SELECT]: FormLazyDropdownControls,
    [FormFieldTypes.TOGGLE]: FormToggleControls,
    [FormFieldTypes.RANGE_SLIDER]: FormRangeSliderControls
};

const FormFieldsControl = ({
    sections,
    formData,
    validationState,
    validationRules,
    formSetting,
    onFormStateChange,
    onFormFieldChange,
    onFieldValidate,
    onFieldBlur,
    onActionChange,
    onFileValidate,
    onImageValidate,
}) => {

    // State
    const { t } = useTranslation();
    const [fieldOverrideProps, setFieldOverrideProps] = useState({});

    const fieldIndex = useMemo(() => {
        const map = new Map();
        sections.forEach((section) => {
            section?.fields?.forEach((field) => {
                map.set(field.dataField, field);
            });

            section?.rowGroup?.forEach((group) => {
                group?.fields?.forEach((field) => {
                    map.set(field.dataField, field);
                });
            });
        });
        return map;
    }, [sections]);

    const getFieldConfig = useCallback(
        (dataField) => fieldIndex.get(dataField), [fieldIndex]
    );

    // Change Action
    const applyFieldChangeAction = useCallback((dataField, updatedData) => {
        const fieldConfig = getFieldConfig(dataField);
        if (!fieldConfig?.changeAction) return updatedData;

        const nextData = { ...updatedData };
        const nextOverrideProps = { ...fieldOverrideProps };

        fieldConfig.changeAction?.resetValue?.forEach(({ dataField, value }) => {
            nextData[dataField] = value;
        });

        fieldConfig.changeAction?.resetFieldSetting?.forEach(
            ({ dependancyField, condition }) => {
                const isMatched =
                    condition.type === "=" &&
                    nextData[dataField] === condition.value;

                dependancyField?.forEach(
                    ({ dataField, updateProps, resetValue }) => {
                        if (isMatched) {
                            nextOverrideProps[dataField] = updateProps;
                            nextData[dataField] = resetValue;
                        } else {
                            delete nextOverrideProps[dataField];
                        }
                    }
                );
            }
        );

        setFieldOverrideProps(nextOverrideProps);
        return nextData;
    }, [fieldOverrideProps, getFieldConfig]);

    // Handlers
    const handleFieldValueChange = useCallback((dataField, value) => {
        let updatedData = { ...formData, [dataField]: value };
        updatedData = applyFieldChangeAction(dataField, updatedData);

        onFormStateChange?.(updatedData);
        onFormFieldChange?.(dataField, updatedData);
    }, [formData, applyFieldChangeAction]);

    const handleFieldBlur = useCallback((dataField) => {
        onFieldValidate?.(dataField);
        onFieldBlur?.(dataField, formData);
    }, [formData]);

    const handleFileFieldBlur = useCallback((dataField, files, fieldSetting) => {
        onFileValidate?.(files, dataField, fieldSetting, formData);
    }, [formData, onFileValidate]);

    const handleImageFieldBlur = useCallback((dataField, files, fieldSetting) => {
        onImageValidate?.(files, dataField, fieldSetting, formData);
    }, [formData, onImageValidate]);

    const handleFieldAction = useCallback((actionName, dataField, actionData, index) => {
        onActionChange?.(actionName, formData, actionData, dataField, index);
    }, [formData]);

    const renderField = useCallback((field, index) => {
        const FieldComponent = FIELD_CONTROL_MAP[field.fieldType];
        if (!FieldComponent) return null;

        // Optional per-field hooks driven by the live form data:
        //   visibleWhen(formData)  → false hides the field
        //   disabledWhen(formData) → true disables the field
        //   getLabel(formData)     → label that follows another field's value
        if (typeof field.visibleWhen === "function" && !field.visibleWhen(formData)) return null;
        const label = typeof field.getLabel === "function" ? field.getLabel(formData) : field.label;
        const isDisabledWhen = typeof field.disabledWhen === "function" && !!field.disabledWhen(formData);

        const { containerCss = "col-md-6" } = field.style || {};
        const isRequired = !!validationRules?.[field.dataField]?.some(
            rule => rule.type === validationTypes.REQUIRE
        );

        const translatedFieldSetting = field.fieldSetting ? {
            ...field.fieldSetting,
            ...(isDisabledWhen && { isDisable: true }),
            placeholder: field.fieldSetting?.isRemoveI18n ? field.fieldSetting.placeholder : resolveI18n(field.fieldSetting.placeholder, t),
            options: field.fieldSetting.options?.map(opt => ({
                ...opt,
                label: opt.label
            })),
        } : field.fieldSetting;

        // ── LAZY_SELECT: auto-inject dependsOn values ──────────────────────────
        // If a lazy dropdown declares dependsOn: ['fieldA', 'fieldB'], we:
        //   1. Build dependentValues = { fieldA: formData.fieldA, fieldB: formData.fieldB }
        //   2. Compute a stable string resetKey from their primitive IDs
        //   3. Inject both into translatedFieldSetting
        // FormLazyDropdownControls uses resetKey in its useEffect to auto-reset
        // when a dependency changes, and passes dependentValues into fetchOptions.
        // Parents need ZERO extra code for dependent dropdowns — just declare dependsOn.
        // disableUntilDependsOnSelected: true keeps the field disabled until every dependsOn field has a value
        // (still mounted, so it clears its own value when a parent changes).
        if (
            field.fieldType === FormFieldTypes.LAZY_SELECT &&
            Array.isArray(field.fieldSetting?.dependsOn) &&
            field.fieldSetting.dependsOn.length > 0
        ) {
            const deps = field.fieldSetting.dependsOn;
            const dependentValues = Object.fromEntries(
                deps.map(dep => [dep, formData?.[dep]])
            );
            // Use primitive value (getValue resolves { value, label } objects to just the id)
            const resetKey = deps
                .map(dep => {
                    const v = formData?.[dep];
                    return (v && typeof v === 'object' ? v.value : v) ?? '';
                })
                .join('||');

            translatedFieldSetting.dependentValues = dependentValues;
            translatedFieldSetting.resetKey = resetKey;

            if (field.fieldSetting.disableUntilDependsOnSelected) {
                const isDependsOnEmpty = deps.some((dep) => {
                    const v = formData?.[dep];
                    const id = v && typeof v === 'object' ? v.value : v;
                    return id === null || id === undefined || id === '';
                });
                if (isDependsOnEmpty) translatedFieldSetting.isDisable = true;
            }
        }

        return (
            <div className={containerCss} key={`${field.dataField}_${index}`}>
                <FieldComponent
                    dataField={field.dataField}
                    labelName={field.fieldSetting?.isRemoveI18n ? label : resolveI18n(label, t)}
                    name={field.id}
                    type={getInputTypeByFieldType(field.fieldType)}
                    value={formData?.[field.dataField] ?? ""}
                    error={validationState.error[field.dataField] || ""}
                    formSetting={formSetting}
                    formData={formData}
                    isRequired={isRequired}
                    fieldSetting={translatedFieldSetting}
                    changeAction={field.changeAction}
                    fieldOverrideProps={fieldOverrideProps?.[field.dataField]}
                    onChange={handleFieldValueChange}
                    onValidation={onFieldValidate}
                    onBlur={handleFieldBlur}
                    onActionChange={handleFieldAction}
                    onFileFleidBlur={handleFileFieldBlur}
                    onImageFieldBlur={handleImageFieldBlur}
                />
            </div>
        );
    }, [formData, validationState, validationRules, fieldOverrideProps, t]);

    // Render Row Groups
    const renderRowGroups = useCallback((rowGroups) => rowGroups.map((rowGroup, index) => {
        const { groupStyle = "col-md-12" } = rowGroup.style || {};
        const GroupWrapper = rowGroup.groupWrapper || React.Fragment;
        const isJSXLiteral = React.isValidElement(GroupWrapper);

        const content = rowGroup.fields.map(renderField);

        return (
            <div key={`rowGroup_${index}`} className={groupStyle}>
                {isJSXLiteral
                    ? React.cloneElement(GroupWrapper, {}, content)
                    : <GroupWrapper>{content}</GroupWrapper>}
            </div>
        );
    }), [renderField]);

    // Render Sections
    const renderSection = useCallback((section, index) => {
        const { sectionStyle = "col-md-12" } = section.style || {};
        const Wrapper = section.wrapperTemplate || React.Fragment;
        const isJSXLiteral = React.isValidElement(Wrapper);

        const content = section.rowGroup
            ? renderRowGroups(section.rowGroup) : section.fields.map(renderField);

        return (
            <div key={`section_${index}`} className={sectionStyle}>
                {isJSXLiteral
                    ? React.cloneElement(Wrapper, {}, content)
                    : <Wrapper>{content}</Wrapper>}
            </div>
        );
    }, [renderRowGroups, renderField]);

    return <>{sections.map(renderSection)}</>;
};

export default memo(FormFieldsControl);